/**
 * Start a design or technical review from the Hub (ADR-0060 D4, ADR-0063 D5 step 3).
 *
 * 🔴 WHY THIS EXISTS. Both review routes were built and deployed with no door in the product, and the
 * technical review is the ONLY automated producer of a protected-stop candidate — so without this, the
 * Confirm / Reject decision on a candidate (the panel above) had nothing to decide on staging, ever.
 *
 * ⚠️ WHAT A RUN DOES, SAID ON SCREEN. It spends model budget, and its findings land as `stated`
 * register findings — proposals. Nothing it produces can open a stop: only an owner confirming a
 * candidate does. A reader who thinks "Run" might halt the project would not press it; one who thinks
 * it is free would press it twice.
 */

import { useState } from 'react';
import { startReview, type ReviewKind } from '../api.js';

const REVIEWS: readonly { kind: ReviewKind; label: string; explains: string }[] = [
  {
    kind: 'technical-review',
    label: 'Run technical review',
    explains:
      'Checks the current set for life-safety and structural-integrity issues. Anything it finds lands ' +
      'as a proposed protected stop for an owner to confirm or reject — it never opens a stop itself.',
  },
  {
    kind: 'design-review',
    label: 'Run design review',
    explains: 'Reviews the current set and lands its findings on this record as register findings.',
  },
];

export function RecordReviews({
  code,
  canRun,
  start = startReview,
}: {
  code: string;
  canRun: boolean;
  /** Injected so a test can drive the click without the network. */
  start?: typeof startReview;
}) {
  const [busy, setBusy] = useState<ReviewKind | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  if (!canRun) return null;

  const run = async (kind: ReviewKind) => {
    setBusy(kind);
    setMessage(null);
    try {
      const r = await start(code, kind);
      setMessage(
        r.ok
          ? 'Queued. Findings appear on this record when the review finishes — usually a few minutes; reload to see them.'
          : r.message,
      );
    } catch {
      setMessage('We couldn’t reach the server — the review was not started. Give it another try.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="card">
      <h3 className="card__title">Reviews</h3>
      <p className="card__note">Each run reads the current drawing set and spends model budget.</p>
      <ul className="reviews">
        {REVIEWS.map((r) => (
          <li key={r.kind} className="reviews__row">
            <button
              type="button"
              className="reviews__run"
              disabled={busy !== null}
              onClick={() => void run(r.kind)}
            >
              {busy === r.kind ? 'Starting…' : r.label}
            </button>
            <span className="muted">{r.explains}</span>
          </li>
        ))}
      </ul>
      {message && <p className="reviews__message">{message}</p>}
    </section>
  );
}
